/**
 * Intake hub design tokens.
 *
 * Values follow the "Payer Handoff MVP" design (claude.ai/design, Sep 2026).
 * The brand indigo is the app theme's `palette.primary.main`; everything else
 * is the design's flat neutral/semantic palette. Keep every hex here so the
 * feature has one place to swap when it moves fully onto the app theme.
 */

export const wf = {
  ink: '#1c1c22',
  body: '#33333d',
  muted: '#6b6b78',
  meta: '#8a8a96',
  faint: '#b0b0be',
  line: '#e2e2ea',
  lineSoft: '#eeeef4',
  surface: '#ffffff',
  surfaceAlt: '#fafafd',
  page: '#f6f6f9',
  /** Brand indigo — matches the app theme primary. */
  blue: '#36229e',
  blueHover: '#241f80',
  blueSoft: '#f0f0fa',
  blueLine: '#cdcdec',
  blueBanner: '#f1f1fb',
  purple: '#36229e',
  purpleSoft: '#f0f0fa',
  amber: '#a8480b',
  amberSoft: '#fff5eb',
  amberLine: '#f2cfa8',
  amberWarn: '#d97706',
  amberWarnSoft: '#fff8ed',
  amberWarnLine: '#f0cfa0',
  amberInk: '#7c3f06',
  green: '#15803d',
  greenSoft: '#eaf6ee',
  greenLine: '#c2e2cd',
  greenBanner: '#eefaf1',
  greenInk: '#14532d',
  red: '#a63244',
  redSoft: '#fdeeee',
  slate: '#4e4e63',
  slateSoft: '#f2f2f7',
  slateLine: '#dcdce6',
  track: '#e4e4ec',
} as const;

/** Card container from the design: white, hairline border, 10px radius. */
export const cardSx = {
  border: `1px solid ${wf.line}`,
  borderRadius: '10px',
  padding: '18px 20px',
  backgroundColor: wf.surface,
} as const;

export const greyCardSx = {
  ...cardSx,
  backgroundColor: wf.slateSoft,
  borderColor: wf.slateLine,
  color: wf.slate,
} as const;

/** Uppercase section eyebrow ("BEFORE YOUR INTAKE", "YOU'LL SIGN"). */
export const overlineSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: wf.meta,
} as const;

/** Table header row from the design's grids. */
export const tableHeadSx = {
  ...overlineSx,
  letterSpacing: '0.07em',
  color: wf.muted,
  backgroundColor: wf.surfaceAlt,
  borderBottom: `1px solid ${wf.lineSoft}`,
} as const;

/** Small grey detail line under a table cell's primary value. */
export const metaSx = { fontSize: '12px', color: wf.meta, mt: '2px' } as const;

/** Small grey footnote under a table or card. */
export const footnoteSx = {
  fontSize: '12px',
  lineHeight: 1.6,
  color: '#9a9aa6',
  maxWidth: 720,
} as const;

/** Group header text. */
export const groupTitleSx = {
  fontSize: '15px',
  fontWeight: 700,
  color: wf.ink,
} as const;

/** Short context under a group header; wraps to remain readable in narrow cards. */
export const groupBlurbSx = {
  fontSize: '13px',
  lineHeight: 1.55,
  color: wf.muted,
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
} as const;

/** The design's four pill tones plus red for errors. */
export type PillTone = 'indigo' | 'amber' | 'green' | 'slate' | 'red';

export const PILL_TONES: Record<PillTone, { fg: string; bg: string; bd: string }> = {
  indigo: { fg: wf.blue, bg: wf.blueSoft, bd: wf.blueLine },
  amber: { fg: wf.amber, bg: wf.amberSoft, bd: wf.amberLine },
  green: { fg: wf.green, bg: wf.greenSoft, bd: wf.greenLine },
  slate: { fg: wf.slate, bg: wf.slateSoft, bd: wf.slateLine },
  red: { fg: wf.red, bg: wf.redSoft, bd: wf.redSoft },
};

/**
 * Legacy chip tones. Prefer `IntakePill` (a themed Chip) in new code; this
 * stays so untouched call sites pick up the design's pill treatment.
 */
export type ChipTone = 'mut' | 'ok' | 'sug' | 'rev' | 'pend' | 'info';

export const CHIP_TONE_TO_PILL: Record<ChipTone, PillTone> = {
  mut: 'slate',
  ok: 'green',
  sug: 'amber',
  rev: 'red',
  pend: 'amber',
  info: 'indigo',
};

export const pillSx = (tone: PillTone) => {
  const t = PILL_TONES[tone];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.03em',
    lineHeight: 1.4,
    borderRadius: '999px',
    padding: '3px 9px',
    whiteSpace: 'nowrap',
    color: t.fg,
    background: t.bg,
    border: `1px solid ${t.bd}`,
  } as const;
};

export const chipSx = (tone: ChipTone) => pillSx(CHIP_TONE_TO_PILL[tone]);
